import { ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { DeviceFrameDesktop, DeviceFramePhone, PlaceholderMedia } from "@/components/DeviceFrames";
import { useLang } from "@/i18n/translations";
import { PROJECTS } from "@/lib/siteConfig";

// Case study shells for projects whose real assets are pending.
// Strictly NO fake screenshots — neutral labeled frames, swappable 1:1 later.
const shellProjects = PROJECTS.filter((p) => !p.hasAssets);

const Shell = ({ project, index }) => {
  const { t } = useLang();
  const flip = index % 2 === 1;
  const copy = t.projects[project.id];

  return (
    <section
      id={`case-${project.id}`}
      data-testid={`case-study-${project.id}`}
      className={`relative py-20 sm:py-24 ${index % 2 === 0 ? "bg-[#05070d]" : "bg-[#070a12] border-y border-white/5"}`}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center`}>
          <Reveal className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
            <p className="text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff] mb-3">
              {t.shells.kicker} — {t.categories[project.categoryKey]}
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.02em] leading-[1.1] text-foreground">
              {copy.positioning}
            </h2>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">{copy.description}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {project.allBadges.map((b) => (
                <Badge key={b} variant="outline" className="bg-white/5 border-white/10 text-foreground/80 font-mono-cs text-[10px] font-normal rounded-full px-2.5">
                  {b}
                </Badge>
              ))}
            </div>
            <div className="mt-7 flex items-center gap-5">
              <a
                data-testid={`case-visit-${project.id}`}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#00d4ff] hover:text-[#33ddff] transition-colors duration-200"
              >
                {project.name} <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
            <p className="mt-5 text-xs font-mono-cs text-muted-foreground/60">{t.shells.assetNote}</p>
          </Reveal>

          <Reveal delay={0.12} className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
            <div className="relative">
              <DeviceFrameDesktop url={project.url.replace("https://", "")} testId={`shell-desktop-frame-${project.id}`}>
                <PlaceholderMedia label={t.shells.desktopLabel} aspect="1920/1137" className="rounded-none border-0" testId={`shell-desktop-placeholder-${project.id}`} />
              </DeviceFrameDesktop>
              <div className="absolute -bottom-8 -right-2 sm:right-6 w-[26%] max-w-[150px]">
                <DeviceFramePhone testId={`shell-phone-frame-${project.id}`}>
                  <PlaceholderMedia label={t.shells.mobileLabel} aspect="840/1817" className="rounded-none border-0" testId={`shell-phone-placeholder-${project.id}`} />
                </DeviceFramePhone>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const CaseStudyShells = () => (
  <>
    {shellProjects.map((p, i) => (
      <Shell key={p.id} project={p} index={i} />
    ))}
  </>
);

// Single-project shell for dedicated subpages (/references/:projectId)
// when the project's real assets are still pending. NO fake assets.
export const PendingCaseStudy = ({ project }) => <Shell project={project} index={0} />;
